import { Component, EventEmitter, Output, inject, OnInit, OnDestroy, HostListener, ElementRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { GlobalSearchService } from '../../../services';
import { GlobalSearchResponseModel, GlobalSearchResult } from '../../../core/models';
import { Subject, debounceTime, distinctUntilChanged, switchMap, takeUntil, of } from 'rxjs';
import { FilterHeaderComponent } from '../../../core/components/filter-header/filter-header.component';

@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, CommonModule, InputTextModule, FilterHeaderComponent, ProgressSpinnerModule],
  templateUrl: './global-search.component.html',
  styleUrl: './global-search.component.scss',
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  private globalSearchService = inject(GlobalSearchService);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();
  private elementRef = inject(ElementRef);

  searchForm!: FormGroup;
  isLoading: boolean = false;
  searchResults: GlobalSearchResponseModel | null = null;
  showDropdown: boolean = false;
  showClearButton: boolean = true;
  showMoreFilters: boolean = false;

  @Output() search = new EventEmitter<GlobalSearchResponseModel | null>();

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.showDropdown = false;
    }
  }

  ngOnInit(): void {
    this.searchForm = this.fb.group({
      globalSearch: [''],
    });

    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          if (term && term.trim().length >= 2) {
            // Garantir que o loading está ativo antes de fazer a busca
            if (!this.isLoading) {
              this.isLoading = true;
            }
            if (!this.showDropdown) {
              this.showDropdown = true;
            }
            return this.globalSearchService.searchWithDefaults(term.trim());
          } else {
            this.isLoading = false;
            this.showDropdown = false;
            this.searchResults = null;
            return of(null as any);
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (results) => {
          if (results) {
            this.searchResults = results;
            this.search.emit(results);
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error in automatic search:', error);
          this.searchResults = null;
          this.search.emit(null);
          this.isLoading = false;
        },
      });

    this.searchForm
      .get('globalSearch')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value && value.trim().length >= 2) {
          this.isLoading = true;
          this.showDropdown = true;
          this.searchResults = null;
        } else {
          this.isLoading = false;
          this.showDropdown = false;
          this.searchResults = null;
        }
        this.searchSubject.next(value);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(): void {
    const searchValue = this.searchForm.get('globalSearch')?.value;
    if (searchValue?.trim()) {
      this.searchSubject.next(searchValue);
    }
  }

  onClear(): void {
    this.searchForm.get('globalSearch')?.setValue('');
    this.searchResults = null;
    this.showDropdown = false;
    this.search.emit(null);
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onSearch();
    }
  }

  onFocus(): void {
    if (this.searchResults && this.searchResults.results.length > 0) {
      this.showDropdown = true;
    }
  }

  onBlur(): void {

  }

  onResultClick(result: GlobalSearchResult): void {
    this.showDropdown = false;

    if (result.type === 'Customer') {
      const targetUrl = `/customers/edit/${result.id}`;

      if (this.router.url.startsWith('/customers/edit/')) {
        const previousReuse = this.router.routeReuseStrategy.shouldReuseRoute;
        const previousOnSameUrl = this.router.onSameUrlNavigation;

        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
        this.router.onSameUrlNavigation = 'reload';

        this.router.navigateByUrl(targetUrl).finally(() => {
          this.router.routeReuseStrategy.shouldReuseRoute = previousReuse;
          this.router.onSameUrlNavigation = previousOnSameUrl;
        });
      } else {
        this.router.navigateByUrl(targetUrl);
      }

      this.showDropdown = false;
      return;
    }

    if (result.type === 'Quote') {
      const phone = this.getPhoneFromResult(result);

      if (phone) {
        this.router.navigate(['/quotes/summary/phone', phone, result.id]);
      } else {
        this.router.navigate(['/quotes/summary', result.id]);
      }
    }
  }

  onViewAll(): void {
    this.showDropdown = false;
    const searchValue = this.searchForm.get('globalSearch')?.value;
    this.router.navigate(['/search'], {
      queryParams: { q: searchValue },
    });
  }

  getHighlightedTitle(result: GlobalSearchResult): string {
    const titleField = result.highlightedFields.find((field) => field.fieldName === 'Name' || field.fieldName === 'Customer' || field.fieldName === 'Quote ID');

    return titleField ? titleField.highlightedValue : result.title;
  }

  getDisplayFields(result: GlobalSearchResult) {
    const fieldsToSkip = ['Phone', 'Email', 'Zip Code', 'Age'];
    const priorityFields = ['Zip Code', 'Age', 'Phone', 'Email', 'Coverage', 'Quote ID'];

    const priorityMatches = result.highlightedFields.filter(field => 
      priorityFields.includes(field.fieldName)
    ).filter(field => !fieldsToSkip.includes(field.fieldName));

    const otherFields = result.highlightedFields.filter(field => 
      !priorityFields.includes(field.fieldName) && 
      field.fieldName !== 'Name' && 
      field.fieldName !== 'Customer'
    ).filter(field => !fieldsToSkip.includes(field.fieldName));

    const combinedFields = [...priorityMatches, ...otherFields];

    return combinedFields.slice(0, 4);
  }

  getContactFields(result: GlobalSearchResult) {
    const contactFieldNames = ['Phone', 'Email', 'Zip Code', 'Age'];

    return contactFieldNames
      .map((name) => result.highlightedFields.find((field) => field.fieldName === name))
      .filter((field): field is typeof result.highlightedFields[number] => !!field);
  }

  applyFilters(): void {
    this.onSearch();
  }

  clearAllFilters(): void {
    this.onClear();
  }

  onShowMoreFiltersChange(show: boolean): void {
    this.showMoreFilters = show;
  }

  private getPhoneFromResult(result: GlobalSearchResult): string | null {
    const phoneField = result.highlightedFields.find((field) => field.fieldName === 'Phone');
    if (!phoneField) {
      return null;
    }

    const raw = phoneField.highlightedValue ?? phoneField.value ?? '';
    const withoutHtml = raw.replace(/<[^>]+>/g, '');
    const digitsOnly = withoutHtml.replace(/\D/g, '');

    return digitsOnly || null;
  }
}
